import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, TextInput, InlineNotification } from '@carbon/react';
import { ArrowLeft, Email, Send } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import './AuthPages.scss';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isDark = theme === 'dark';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    
    const { error: resetError } = await resetPassword(email);
    
    if (resetError) {
      setError(resetError.message || 'Failed to send reset email');
      setLoading(false);
      return;
    }
    
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`} data-testid="forgot-password-page">
      <div className="auth-page__container auth-page__container--centered">
        <div className="auth-card">
          <Button
            kind="ghost"
            size="sm"
            renderIcon={ArrowLeft}
            onClick={() => navigate('/login')}
            className="auth-card__back-btn"
          >
            Back to Login
          </Button>

          <div className="auth-card__header auth-card__header--icon">
            <div className="auth-card__icon">
              <Email size={28} />
            </div>
            <h2 className="auth-card__title">Forgot Password?</h2>
            <p className="auth-card__subtitle">No worries, we'll send you reset instructions.</p>
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError('')}
              className="auth-card__notification"
            />
          )}

          {submitted ? (
            <div className="auth-card__success">
              <InlineNotification
                kind="success"
                title="Email Sent!"
                subtitle={`We've sent a password reset link to ${email}`}
                hideCloseButton
                className="auth-card__notification"
              />
              <p className="auth-card__success-text">
                Didn't receive the email? Check your spam folder or
              </p>
              <Button kind="tertiary" onClick={() => setSubmitted(false)} className="auth-card__btn">
                Try another email
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-card__form">
              <div className="auth-card__fields">
                <TextInput
                  id="email"
                  type="email"
                  labelText="Email Address"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="auth-card__actions">
                <Button
                  kind="primary"
                  type="submit"
                  className="auth-card__btn"
                  disabled={loading}
                  renderIcon={Send}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          )}

          <p className="auth-card__footer">
            Remember your password?{' '}
            <span onClick={() => navigate('/login')} className="auth-card__link">Sign in</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
